import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import ReviewListItem from "./ReviewListItem";
import PendingPullRequests from "./PendingPullRequests";
import StartDecisionReview from "./StartDecisionReview";
import Skeleton from "../ui/Skeleton";
import InlineAlert from "../ui/InlineAlert";
import { matchesReviewFilter, normalizeWorkspaceReviews } from "./reviewDisplay";
import { REVIEWS_DESCRIPTION } from "../copy/companyBrainCopy";

function compareReviews(a, b) {
  const rank = (item) => {
    if (item.activity === "running") return 0;
    if (item.needsAttention && item.currency !== "stale") return 1;
    if (item.currency === "stale") return 3;
    return 2;
  };
  const delta = rank(a) - rank(b);
  if (delta) return delta;
  return new Date(b.updatedAt || b.completedAt || 0) - new Date(a.updatedAt || a.completedAt || 0);
}

const STATUS = [
  ["current", "Recent"],
  ["running", "Running"],
  ["all", "All"],
];

export default function ReviewsPage({ filter = "current", type = "all", repositoryId = "", repositoryIds = [], repositoryCount = 0, mutateAllowed = true, onFilter, onType, onStarted, onOpenDecision, onOpenAcceptance }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [setup, setSetup] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/workspace/reviews")
      .then((reviews) => {
        if (!cancelled) setItems(normalizeWorkspaceReviews(reviews.data));
      })
      .catch(() => {
        if (!cancelled) setError("Reviews could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/workspace/setup").then((response) => {
      if (!cancelled) setSetup(response.data || {});
    }).catch(() => {
      if (!cancelled) setSetup({});
    });
    return () => {
      cancelled = true;
    };
  }, [repositoryCount]);

  const allowedIds = repositoryIds.length ? repositoryIds.map(String) : repositoryId ? [String(repositoryId)] : [];
  const scoped = (items || []).filter((item) => !allowedIds.length || allowedIds.includes(String(item.repoId || "")));
  const bothTypes = useMemo(() => {
    const types = new Set(scoped.map((item) => item.type));
    return types.has("decision") && types.has("acceptance");
  }, [scoped]);

  const activeFilter = filter === "needs-attention" ? "current" : filter;
  const filtered = scoped.filter((item) => matchesReviewFilter(item, activeFilter, bothTypes ? type : "all"));
  const visible = [...filtered].sort(compareReviews).filter((item) => {
    if (!query.trim()) return true;
    const blob = `${item.owner} ${item.name} ${item.title} ${item.pullNumber}`.toLowerCase();
    return blob.includes(query.trim().toLowerCase());
  });
  const deletableVisible = visible.filter((item) => item.type === "decision" || item.type === "acceptance");
  const selectedVisible = deletableVisible.filter((item) => selectedIds.has(item.id));
  const allVisibleSelected = deletableVisible.length > 0 && selectedVisible.length === deletableVisible.length;

  function toggleSelect(item) {
    if (item.type !== "decision" && item.type !== "acceptance") return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  }

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const item of deletableVisible) next.delete(item.id);
      } else {
        for (const item of deletableVisible) next.add(item.id);
      }
      return next;
    });
  }

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeFilter, type, query, repositoryId]);

  async function archiveReviews(targets) {
    const chosen = (Array.isArray(targets) ? targets : [])
      .map((target) => (typeof target === "string" ? (items || []).find((item) => item.id === target) : target))
      .filter((item) => item && (item.type === "decision" || item.type === "acceptance"));
    if (!chosen.length || !mutateAllowed) return;
    const label = chosen.length === 1 ? "this review" : `these ${chosen.length} reviews`;
    if (
      !window.confirm(
        `Remove ${label} from the inbox? Billing, GitHub Checks, and audit history stay. Running reviews cannot be removed.`
      )
    ) {
      return;
    }
    setDeleting(true);
    setDeleteError("");
    try {
      const decisionIds = chosen.filter((item) => item.type === "decision").map((item) => String(item.id));
      const acceptanceIds = chosen.filter((item) => item.type === "acceptance").map((item) => String(item.id));
      const settled = await Promise.allSettled([
        decisionIds.length ? api.post("/decision-reviews/archive", { reviewIds: decisionIds }) : Promise.resolve(null),
        acceptanceIds.length ? api.post("/acceptance-reviews/archive", { reviewIds: acceptanceIds }) : Promise.resolve(null),
      ]);
      const removed = new Set();
      const failures = [];
      for (const outcome of settled) {
        if (outcome.status !== "fulfilled" || !outcome.value?.data?.results) {
          if (outcome.status === "rejected") failures.push("request");
          continue;
        }
        for (const row of outcome.value.data.results) {
          if (row.status === "archived" || row.status === "already_archived") removed.add(String(row.id));
          else if (row.status === "running") failures.push("running");
          else if (row.status === "not_found" || row.status === "invalid") failures.push(row.status);
        }
      }
      setItems((prev) => (prev || []).filter((item) => !removed.has(String(item.id))));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of removed) next.delete(id);
        return next;
      });
      if (failures.length && !removed.size) {
        setDeleteError(
          failures.includes("running")
            ? "A running review cannot be removed from the list."
            : "Reviews could not be removed from the list."
        );
      } else if (failures.length) {
        setDeleteError("Some reviews could not be removed. Running reviews stay in the list.");
      }
    } catch {
      setDeleteError("Reviews could not be removed from the list.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="rr-page rr-inbox" data-testid="reviews-inbox">
      <header className="rr-page-head">
        <div>
          <h1>Reviews</h1>
          <p>{REVIEWS_DESCRIPTION} No repository indexing required.</p>
        </div>
      </header>
      <StartDecisionReview hero mutateAllowed={mutateAllowed} onStarted={onStarted} />
      <PendingPullRequests
        mutateAllowed={mutateAllowed}
        repositoryId={repositoryId}
        repositoryIds={repositoryIds}
        onStarted={onStarted}
      />
      {setup?.context?.needsAttention > 0 ? (
          <Link className="rr-context-attention" to={repositoryId ? `/brain?repository=${repositoryId}&lens=attention` : "/brain?lens=attention"}>
          <span className="guide-learning-mark" aria-hidden="true">✦</span>
          <span><strong>{setup.context.needsAttention} context {setup.context.needsAttention === 1 ? "item needs" : "items need"} your attention</strong><small>Resolve a conflict or confirm a weak inference before it can mislead a review.</small></span>
          <b>Open Company Brain →</b>
        </Link>
      ) : null}
      <section className="rr-review-history">
        <div className="rr-section-heading">
          <div><span className="rr-eyebrow">Review inbox</span><h2>{scoped.length ? "Recent pull requests" : "Your reviews will appear here"}</h2></div>
          {scoped.length ? <span className="rr-muted">{scoped.length} total</span> : null}
        </div>
      {scoped.length ? (
        <div className="rr-filters" role="group" aria-label="Review status">
          {STATUS.map(([id, label]) => (
            <button key={id} type="button" className="rr-chip" aria-pressed={activeFilter === id} onClick={() => onFilter?.(id)}>
              {label}
            </button>
          ))}
          {bothTypes ? (
            <select className="rr-type-select" aria-label="Review type" value={type === "acceptance" || type === "decision" ? type : "all"} onChange={(event) => onType?.(event.target.value)}>
              <option value="all">All types</option>
              <option value="decision">Decision</option>
              <option value="acceptance">Acceptance</option>
            </select>
          ) : null}
          {scoped.length >= 8 ? (
            <input aria-label="Search reviews" value={query} placeholder="Search reviews" onChange={(event) => setQuery(event.target.value)} />
          ) : null}
          {mutateAllowed && selectedVisible.length ? (
            <button
              type="button"
              className="rr-chip rr-inbox-bulk-delete"
              disabled={deleting}
              onClick={() => archiveReviews(selectedVisible)}
            >
              Remove selected ({selectedVisible.length})
            </button>
          ) : null}
        </div>
      ) : null}
      {error ? <InlineAlert tone="alert">{error}</InlineAlert> : null}
      {deleteError ? <InlineAlert tone="alert">{deleteError}</InlineAlert> : null}
      {items === null && !error ? <Skeleton label="Loading reviews" /> : null}
      {items && !scoped.length ? (
        <div className="rr-inbox-empty">
          <h3>{repositoryId ? "No reviews for this repository" : "Ready for your first pull request"}</h3>
          <p>The review above starts immediately and will teach the Company Brain as it reads product evidence.</p>
        </div>
      ) : null}
      {visible.length ? (
        <div className={`rr-inbox-table${mutateAllowed ? " has-actions" : ""}`}>
          <div className="rr-inbox-head">
            {mutateAllowed ? (
              <label className="rr-inbox-select">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  disabled={!deletableVisible.length || deleting}
                  aria-label="Select all visible reviews"
                  onChange={toggleSelectAllVisible}
                />
              </label>
            ) : null}
            {mutateAllowed ? (
              <div className="rr-inbox-open-head" aria-hidden="true">
                <span>Pull request</span>
                <span>Result</span>
                <span>Findings</span>
                <span>Updated</span>
              </div>
            ) : (
              <>
                <span>Pull request</span>
                <span>Result</span>
                <span>Findings</span>
                <span>Updated</span>
              </>
            )}
            {mutateAllowed ? <span className="rr-sr">Actions</span> : null}
          </div>
          {visible.map((item) => (
            <ReviewListItem
              key={`${item.type}-${item.id}`}
              item={item}
              showType={bothTypes}
              selectable={mutateAllowed}
              selected={selectedIds.has(item.id)}
              onToggleSelect={toggleSelect}
              onDelete={mutateAllowed ? (chosen) => archiveReviews([chosen]) : undefined}
              deleting={deleting}
              onOpen={(chosen) => (chosen.type === "acceptance" ? onOpenAcceptance?.(chosen) : onOpenDecision?.(chosen))}
            />
          ))}
        </div>
      ) : null}
      </section>
    </div>
  );
}
